import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronUp, FiCheckCircle, FiCircle, FiAlertCircle, FiLayers, FiCheckSquare, FiTrash2 } = FiIcons;

// Colors from Network.jsx for consistency
const CLUSTER_COLORS = [
  '#5470c6', '#91cc75', '#fac858', '#ee6666', 
  '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'
];

const LABELS = {
  1: "Stimme gar nicht zu",
  2: "Stimme nicht zu",
  3: "Stimme eher nicht zu",
  4: "Neutral",
  5: "Stimme eher zu",
  6: "Stimme zu",
  7: "Stimme voll und ganz zu"
};

// Helper Component for Slider Interaction
const QuestionSlider = ({ questionId, initialValue, onSave, status }) => {
  const [value, setValue] = useState(initialValue || 0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    // Only update from props if not currently interacting
    if (!isDragging) {
      setValue(initialValue || 0);
    }
  }, [initialValue, isDragging]);

  const handleChange = (e) => {
    setValue(parseInt(e.target.value, 10));
  };

  const handleMouseDown = () => setIsDragging(true);
  
  const handleCommit = () => {
    setIsDragging(false);
    if (value !== initialValue && value > 0) {
      onSave(questionId, value);
    }
  };

  const getBackgroundStyle = () => {
    if (!value || value === 0) return { background: '#e5e7eb' }; // gray-200
    // Percentage for 1-7 range
    const percentage = ((value - 1) / 6) * 100;
    return {
      background: `linear-gradient(to right, #152627 0%, #152627 ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`
    };
  };

  return (
    <div className="w-full max-w-3xl py-2">
      <div className="mb-0">
         <div className="flex justify-between items-end mb-1">
             <span className="text-sm font-medium text-gray-700">Ihre Bewertung:</span>
             <span className={`text-sm font-bold ${value > 0 ? 'text-brand-secondary' : 'text-gray-400'}`}>
                {value > 0 ? `${value} - ${LABELS[value]}` : 'Nicht bewertet'}
             </span>
         </div>

         <div className="relative h-6 flex items-center">
            <input
                type="range"
                min="1"
                max="7"
                step="1"
                value={value || 1} // Visual default to 1 if 0
                onChange={handleChange}
                onMouseDown={handleMouseDown}
                onTouchStart={handleMouseDown}
                onMouseUp={handleCommit}
                onTouchEnd={handleCommit}
                style={getBackgroundStyle()}
                className="custom-range-slider"
            />
         </div>
      </div>
      
      <div className="flex justify-between text-[10px] sm:text-xs text-gray-400 text-center px-0">
        {Object.entries(LABELS).map(([key, label]) => (
            <div key={key} className={`flex flex-col items-center w-6 justify-start pt-1 ${value == key ? 'text-brand-secondary font-bold' : ''}`}>
                <span className="hidden sm:block">{key}</span>
            </div>
        ))}
      </div>

      <div className="mt-1 h-5 text-right">
          {status === 'saving' && (
            <span className="text-xs text-gray-500 flex items-center justify-end animate-pulse">
              <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
              Speichert...
            </span>
          )}
          {status === 'success' && (
            <span className="text-xs text-green-600 flex items-center justify-end font-medium transition-opacity duration-500">
              <SafeIcon icon={FiCheckCircle} className="w-3 h-3 mr-1" />
              Gespeichert
            </span>
          )}
          {status === 'error' && (
            <span className="text-xs text-red-500 flex items-center justify-end font-medium">
              <SafeIcon icon={FiAlertCircle} className="w-3 h-3 mr-1" />
              Fehler
            </span>
          )}
      </div>
    </div>
  );
};

const Evaluation = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeElements, setActiveElements] = useState([]);
  const [questionsByElement, setQuestionsByElement] = useState({});
  const [userEvaluations, setUserEvaluations] = useState({});
  const [expandedElement, setExpandedElement] = useState(null);
  const [savingState, setSavingState] = useState({});
  const [error, setError] = useState(null);
  
  // New state for clusters
  const [clusters, setClusters] = useState([]);
  const [clusterRelations, setClusterRelations] = useState([]);
  const [activeClusterId, setActiveClusterId] = useState(null);

  useEffect(() => {
    if (user?.teamId && user?.id) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [teamElementsRes, allElementsRes, allQuestionsRes, evaluationsRes, clustersRes, relationsRes] = await Promise.all([
        apiService.getTeamUsesElement({ team_id: user.teamId, limit: 1000 }),
        apiService.getElements({ limit: 1000 }),
        apiService.getQuestions({ limit: 1000 }),
        apiService.getEvaluations({ user_id: user.id, limit: 1000 }),
        apiService.getClusters(),
        apiService.getClusterElementRelations()
      ]);

      let elements = [];
      let activeRelations = [];

      if (teamElementsRes.status === 'success' && allElementsRes.status === 'success') {
        activeRelations = Array.isArray(teamElementsRes.data) 
          ? teamElementsRes.data.filter(r => r.is_used == 1) 
          : [];
        
        const activeElementIds = activeRelations.map(r => r.element_id);
        
        elements = Array.isArray(allElementsRes.data) 
          ? allElementsRes.data.filter(e => activeElementIds.includes(e.id))
          : [];
        
        setActiveElements(elements);

        if (allQuestionsRes.status === 'success' && Array.isArray(allQuestionsRes.data)) {
          const questionsMap = {};
          allQuestionsRes.data.forEach(q => {
            if (activeElementIds.includes(q.element_id)) {
              if (!questionsMap[q.element_id]) {
                questionsMap[q.element_id] = [];
              }
              questionsMap[q.element_id].push(q);
            }
          });
          setQuestionsByElement(questionsMap);
        }

        if (evaluationsRes.status === 'success' && Array.isArray(evaluationsRes.data)) {
            const evalsMap = {};
            evaluationsRes.data.forEach(ev => {
                evalsMap[ev.question_id] = ev;
            });
            setUserEvaluations(evalsMap);
        }
      }

      let fetchedClusters = [];
      if (clustersRes.status === 'success' && Array.isArray(clustersRes.data)) {
        fetchedClusters = clustersRes.data;
        setClusters(fetchedClusters);
      }
      
      let fetchedRelations = [];
      if (relationsRes.status === 'success' && Array.isArray(relationsRes.data)) {
        fetchedRelations = relationsRes.data;
        setClusterRelations(fetchedRelations);
      }

      // Calculate initial active cluster
      const grouped = getClusteredActiveElements(elements, fetchedClusters, fetchedRelations);
      if (grouped.length > 0) {
        setActiveClusterId(grouped[0].id);
      }

    } catch (err) {
      console.error("Error fetching evaluation data:", err);
      setError("Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvaluation = async (questionId, value) => {
    if (!value) return;

    setSavingState(prev => ({ ...prev, [questionId]: 'saving' }));
    
    // Optimistic Update
    const previousEval = userEvaluations[questionId];
    setUserEvaluations(prev => ({
       ...prev,
       [questionId]: { 
         ...prev[questionId], 
         evaluation_value: parseInt(value),
         id: prev[questionId]?.id 
       }
    }));

    try {
      const existingEval = userEvaluations[questionId];
      let res;

      if (existingEval && existingEval.id) {
        res = await apiService.updateEvaluation(existingEval.id, {
          evaluation_value: parseInt(value)
        });
      } else {
        res = await apiService.createEvaluation({
          question_id: parseInt(questionId),
          user_id: parseInt(user.id),
          evaluation_value: parseInt(value)
        });
        
        if (res.status === 'success' && (res.data?.id || res.id)) {
           const newId = res.data?.id || res.id;
           setUserEvaluations(prev => ({
             ...prev,
             [questionId]: { ...prev[questionId], id: newId }
           }));
        }
      }

      setSavingState(prev => ({ ...prev, [questionId]: 'success' }));
      
      setTimeout(() => {
        setSavingState(prev => {
            const newState = { ...prev };
            delete newState[questionId];
            return newState;
        });
      }, 2000);

    } catch (err) {
      console.error("Error saving evaluation:", err);
      setSavingState(prev => ({ ...prev, [questionId]: 'error' }));
    }
  };

  const handleDeleteClusterEvaluations = async (cluster) => {
    if (!window.confirm(`Möchten Sie wirklich alle Ihre Bewertungen für das Cluster "${cluster.title}" löschen?`)) {
      return;
    }

    setLoading(true);

    try {
      // Collect all question IDs belonging to elements in this cluster that have user evaluations
      const evalsToDelete = [];
      const questionsToReset = [];

      cluster.elements.forEach(element => {
        const questions = questionsByElement[element.id] || [];
        questions.forEach(q => {
          const userEval = userEvaluations[q.id];
          if (userEval && userEval.id) {
            evalsToDelete.push(userEval.id);
            questionsToReset.push(q.id);
          }
        });
      });

      if (evalsToDelete.length === 0) {
        alert("Keine Bewertungen zum Löschen gefunden.");
        setLoading(false);
        return;
      }

      // Execute deletions
      await Promise.all(evalsToDelete.map(id => apiService.deleteEvaluation(id)));

      // Update local state
      setUserEvaluations(prev => {
        const newState = { ...prev };
        questionsToReset.forEach(qId => {
          delete newState[qId];
        });
        return newState;
      });

      // alert("Bewertungen erfolgreich gelöscht.");

    } catch (err) {
      console.error("Error deleting evaluations:", err);
      alert("Fehler beim Löschen der Bewertungen.");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (elementId) => {
    setExpandedElement(expandedElement === elementId ? null : elementId);
  };

  // Helper to group active elements by cluster
  // Now accepts arguments to be usable inside fetchData as well
  const getClusteredActiveElements = (elementsList = activeElements, clustersList = clusters, relationsList = clusterRelations) => {
    const grouped = {};
    const noCluster = [];

    // Initialize groups for all clusters with color assignment
    clustersList.forEach((c, index) => {
      grouped[c.id] = { 
        ...c, 
        elements: [],
        color: CLUSTER_COLORS[index % CLUSTER_COLORS.length]
      };
    });

    elementsList.forEach(element => {
      const relation = relationsList.find(r => r.element_id === element.id);
      if (relation && grouped[relation.cluster_id]) {
        grouped[relation.cluster_id].elements.push(element);
      } else {
        noCluster.push(element);
      }
    });

    const result = Object.values(grouped).filter(g => g.elements.length > 0);
    
    // Sort clusters alphabetically optionally, or keep ID order
    // result.sort((a, b) => a.title.localeCompare(b.title));

    if (noCluster.length > 0) {
      result.push({
        id: 'none',
        title: 'Allgemein',
        description: 'Elemente ohne spezifische Zuordnung',
        elements: noCluster,
        color: '#6b7280' // Default gray
      });
    }
    
    return result;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full p-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex flex-col items-center justify-center text-red-500">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 mb-2" />
        <p>{error}</p>
      </div>
    );
  }

  const clusteredElements = getClusteredActiveElements();

  return (
    <div className="p-6 w-full">
      <div className="mb-6 flex-shrink-0">
        <h1 className="text-2xl font-bold text-brand-dark flex items-center">
          <SafeIcon icon={FiCheckSquare} className="w-7 h-7 mr-3 text-brand-secondary" />
          Bewertungen
        </h1>
        <p className="text-gray-600 mt-2">
          Bewerten Sie die Fragen zu den aktiven Elementen Ihres Teams auf einer Skala von 1 bis 7.
        </p>
      </div>

      {activeElements.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <p>Aktuell sind keine Elemente für Ihr Team aktiviert.</p>
          <p className="text-sm mt-2">Bitten Sie Ihren Team-Administrator, Module freizuschalten.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex flex-wrap -mb-px px-2" aria-label="Tabs">
               {clusteredElements.map(cluster => (
                  <button
                     key={cluster.id}
                     onClick={() => setActiveClusterId(cluster.id)}
                     className={`
                       group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                       ${activeClusterId === cluster.id
                         ? 'border-brand-secondary text-brand-secondary'
                         : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                     `}
                  >
                     {cluster.title}
                  </button>
               ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Active Cluster Content */}
            {clusteredElements.map((cluster) => {
              if (cluster.id !== activeClusterId) return null;

              return (
                  <div 
                    key={cluster.id} 
                    className="rounded-xl p-4 sm:p-6 border transition-colors duration-200 animate-fadeIn"
                    style={{
                      backgroundColor: cluster.id === 'none' ? '#f9fafb' : `${cluster.color}15`, // ~8% opacity
                      borderColor: cluster.id === 'none' ? '#f3f4f6' : `${cluster.color}40` // ~25% opacity
                    }}
                  >
                     <div className="mb-6 flex justify-between items-start">
                        <div>
                            <h2 
                              className="text-xl font-bold flex items-center gap-2"
                              style={{ color: cluster.id === 'none' ? '#1f2937' : cluster.color }}
                            >
                              <SafeIcon icon={FiLayers} className="w-5 h-5" />
                              {cluster.title}
                            </h2>
                            {cluster.description && (
                              <p className="text-sm text-gray-600 mt-1">{cluster.description}</p>
                            )}
                        </div>
                        <button
                          onClick={() => handleDeleteClusterEvaluations(cluster)}
                          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full transition-colors"
                          title="Alle Bewertungen in diesem Cluster löschen"
                        >
                           <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                        </button>
                     </div>
      
                     <div className="space-y-4">
                        {cluster.elements.map((element) => {
                          const questions = questionsByElement[element.id] || [];
                          const isExpanded = expandedElement === element.id;
                          
                          const answeredCount = questions.filter(q => userEvaluations[q.id]).length;
                          const totalCount = questions.length;
                          const isComplete = totalCount > 0 && answeredCount === totalCount;
      
                          return (
                            <div key={element.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                              <button
                                onClick={() => toggleExpand(element.id)}
                                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none"
                              >
                                <div className="flex items-center">
                                  <div className={`mr-4 flex-shrink-0 ${isComplete ? 'text-brand-primary' : 'text-gray-300'}`}>
                                    <SafeIcon icon={isComplete ? FiCheckCircle : FiCircle} className="w-6 h-6" />
                                  </div>
                                  <div className="text-left">
                                    <h3 className="text-lg font-medium text-gray-900">{element.title}</h3>
                                    <p className="text-sm text-gray-500">
                                      {answeredCount} von {totalCount} Fragen beantwortet
                                    </p>
                                  </div>
                                </div>
                                <SafeIcon 
                                  icon={isExpanded ? FiChevronUp : FiChevronDown} 
                                  className="w-5 h-5 text-gray-400" 
                                />
                              </button>
      
                              {isExpanded && (
                                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/30">
                                  {element.description && (
                                    <div className="mb-6 text-brand-secondary text-sm bg-brand-primary/5 p-4 rounded-md border border-brand-primary/10">
                                      {element.description}
                                    </div>
                                  )}
      
                                  {questions.length === 0 ? (
                                    <p className="text-gray-500 italic">Keine Fragen für dieses Element hinterlegt.</p>
                                  ) : (
                                    <div className="space-y-8">
                                      {questions.map((question) => {
                                        const currentEval = userEvaluations[question.id];
                                        const currentValue = currentEval ? currentEval.evaluation_value : 0;
                                        const status = savingState[question.id];
      
                                        return (
                                          <div key={question.id} className="pb-4 border-b last:border-0 border-gray-200">
                                            <p className="text-gray-800 font-medium mb-3">{question.text}</p>
                                            
                                            <QuestionSlider 
                                              questionId={question.id}
                                              initialValue={currentValue}
                                              onSave={handleSaveEvaluation}
                                              status={status}
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                     </div>
                  </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Evaluation;